"use client";

import { FC, useCallback, useContext, useMemo, useState } from "react";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import { Svg } from "components/base/svg/Svg";
import { UserAccountContextV2 } from "contexts";
import Translate from "components/base/translate/Translate";
import dynamic from "next/dynamic";
import Button from "components/base/button/Button";
import { Chars } from "utils/strings";
import headerStyles from "./header.module.sass";
import styles from "./personal-details.module.sass";
import { UserDetails } from "./user-details/UserDetails";

const VerificationMethods = dynamic(() => import("./verification-methods/VerificationMethods"), {
    ssr: false,
});

export type DetailsCurrentView = "main" | "verification";

type IHeaderProps = {
    currentView: DetailsCurrentView;
    setCurrentView: (view: DetailsCurrentView) => void;
};

const Header: FC<IHeaderProps> = ({ currentView, setCurrentView }) => {
    const { userId, username, isUserVerified } = useContext(UserAccountContextV2);

    const handleCopy = useCallback(() => {
        if (userId) {
            void navigator.clipboard.writeText(userId.toString());
        }
    }, [userId]);
    return (
        <>
            <div className={headerStyles.header}>
                <h2>
                    <Translate
                        tid={
                            currentView === "verification"
                                ? "account_details_verification_heading"
                                : "account_details_personal_heading"
                        }
                        namespace={TranslationScopes.UserAccount}
                    />
                </h2>
                <span className={headerStyles.userIcon}>
                    <Svg
                        src={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-my-details.svg`}
                        className="svg-icon"
                        prefixAppName
                    />
                    {!isUserVerified && (
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/question-icon.svg`}
                            className="svg-icon-unverified"
                        />
                    )}
                </span>
                <Button
                    onClick={handleCopy}
                    type="button"
                    variant="custom"
                    className={headerStyles.userInfoCopy}>
                    <Translate tid="account_id" namespace={TranslationScopes.UserAccount} />:
                    {Chars.nbsp}
                    {userId} / {username}
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Wallet}/copy.svg`}
                        className="svg-icon"
                    />
                </Button>
                {currentView === "verification" && (
                    <Button
                        type="button"
                        variant="custom"
                        className={headerStyles.backButton}
                        onClick={() => setCurrentView("main")}>
                        <Translate
                            tid="account_details_back_button"
                            namespace={TranslationScopes.UserAccount}
                        />
                    </Button>
                )}
            </div>
        </>
    );
};

type IProps = {
    defaultView: DetailsCurrentView;
};

const PersonalDetailsMobile: FC<IProps> = ({ defaultView }) => {
    const { isUserVerified } = useContext(UserAccountContextV2);

    const [currentView, setCurrentView] = useState<DetailsCurrentView>(defaultView);

    const renderCurrentView = useMemo(() => {
        switch (currentView) {
            case "verification":
                return <VerificationMethods />;
            default:
                return <UserDetails onVerificationClick={() => setCurrentView("verification")} />;
        }
    }, [currentView]);

    return (
        <div className={styles.details} data-testid="personal-details-container">
            <Header currentView={currentView} setCurrentView={setCurrentView} />
            {!isUserVerified && (
                <p className={styles.verifyMessage}>
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-warning.svg`}
                        className="svg-icon"
                    />
                    <Translate
                        tid="account_details_verify_message"
                        namespace={TranslationScopes.UserAccount}
                    />
                </p>
            )}
            {renderCurrentView}
        </div>
    );
};

export default PersonalDetailsMobile;
